import { useState } from "react";
import { Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { payWithRazorpay } from "@/lib/razorpay";
import { useAm } from "@/components/am/shared";

export default function StatusControls({ level, x }) {
  const { setTree, tree } = useAm();
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const [ok, setOk] = useState({ spend: false, fee: false });
  const [busy, setBusy] = useState(false);
  if (!x.meta_id) return null;
  const eff = x.meta_status?.configured_status || x.meta_status?.effective_status;
  const run = async (status) => {
    setBusy(true);
    try {
      let r = (await api.post(`/ads-manager/${level}/${x.id}/status`, { status, confirm_spend: ok.spend, confirm_fee: ok.fee })).data;
      if (r.payment) r = await payWithRazorpay(r.payment, me, `/ads-manager/campaigns/${tree.campaign.id}/fee/verify/${r.payment.id}`, `Convenience fee - ${tree.campaign.name}`);
      if (r.reconciled) { toast.success("Payment confirmed. Press the button again to send the status to Meta."); setOpen(false); return; }
      if (r.campaign) setTree(r);
      toast.success(status === "ACTIVE" ? "Sent ACTIVE to Meta. Delivery starts only after Meta approves the ad." : "Paused on Meta");
      setOpen(false);
    } catch (e) { toast.error(errMsg(e) || e.message); } finally { setBusy(false); }
  };
  return (
    <>
      {eff === "ACTIVE"
        ? <Button size="sm" variant="secondary" disabled={busy} onClick={() => run("PAUSED")} data-testid={`${level}-pause`}><Pause className="h-3.5 w-3.5 mr-1" />Pause</Button>
        : <Button size="sm" disabled={busy} onClick={() => setOpen(true)} data-testid={`${level}-activate`}><Play className="h-3.5 w-3.5 mr-1" />Turn on</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="activate-dialog">
          <DialogHeader><DialogTitle>Turn on "{x.name}" on Meta?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">This starts real ad delivery and spending on your own Meta ad account once Meta approves the ads. {level === "campaign" ? "All its ad sets and ads will be turned on." : level === "adset" ? "All ads in this ad set will be turned on; the campaign must also be on." : "Its campaign and ad set must also be on."}</p>
          <label className="flex gap-2 text-sm"><Checkbox checked={ok.spend} onCheckedChange={(v) => setOk({ ...ok, spend: !!v })} data-testid="activate-confirm-spend" />I approve real Meta ad spend, billed by Meta to my ad account.</label>
          <label className="flex gap-2 text-sm"><Checkbox checked={ok.fee} onCheckedChange={(v) => setOk({ ...ok, fee: !!v })} data-testid="activate-confirm-fee" />I approve the LaunchX360 convenience fee shown in Review (separate from Meta ad spend).</label>
          <Button disabled={!ok.spend || !ok.fee || busy} onClick={() => run("ACTIVE")} data-testid="activate-submit">Confirm and turn on</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
